import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AppPassword,
  AppPasswordList,
  CreatedAppPassword,
  Folder,
  Forwarding,
  ForwardingPolicy,
  MailRule,
  MailServers,
  MailSettings,
  MigrationJob,
  MigrationSourceRow,
  RecoveryStatus,
  Vacation,
} from 'zitimail-shared-types';

import { api, describeError } from '../admin/api';
import {
  ErrorText,
  inMotion,
  Progress,
  StatusBadge,
  statusNote,
  usePoll,
} from '../admin/migrations';
import { when } from '../admin/ui';
import { mail, type ScheduledList } from '../api/mail';
import { edited, RulesPanel, type EditedRule } from './Rules';
import { canNotify, notifyWanted, setNotify } from './notify';
import { SignatureEditor } from './SignatureEditor';
import { TwoStepSignIn } from './TwoStep';

const NO_FORWARDING: Forwarding = { enabled: false, address: '', keepCopy: true };

/**
 * Rules and out-of-office. Both run on the server (Sieve), so they apply
 * with no app open — and the same rules are what Thunderbird's Sieve editor
 * would show over ManageSieve.
 */
export function Settings({
  tenant,
  onBack,
  newRule,
}: {
  tenant: string;
  onBack: () => void;
  /** A rule made from a message: added at the end, unsaved, for the user to finish. */
  newRule?: MailRule | undefined;
}): JSX.Element {
  const [settings, setSettings] = useState<MailSettings | null>(null);
  const [rules, setRules] = useState<EditedRule[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [managed, setManaged] = useState(true);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [saving, setSaving] = useState(false);
  // What the server has, to tell unsaved changes from saved ones.
  const [saved, setSaved] = useState<string | null>(null);
  const snapshot = (s: MailSettings, r: readonly EditedRule[]) =>
    JSON.stringify({ ...s, rules: r.map((x) => x.rule) });
  const dirty = settings !== null && saved !== null && snapshot(settings, rules) !== saved;

  // Closing the tab with unsaved changes asks first (the browser's own words).
  useEffect(() => {
    if (!dirty) {
      return undefined;
    }
    const stay = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', stay);
    return () => window.removeEventListener('beforeunload', stay);
  }, [dirty]);

  const leave = () => {
    if (dirty && !window.confirm('Leave Settings without saving your changes?')) {
      return;
    }
    onBack();
  };

  useEffect(() => {
    mail
      .settings()
      .then(({ managed: m, ...s }) => {
        setSettings(s);
        setSaved(
          snapshot(
            s,
            s.rules.map((rule) => edited(rule)),
          ),
        );
        setRules([
          ...s.rules.map((rule) => edited(rule)),
          ...(newRule ? [edited(newRule, 'folder')] : []),
        ]);
        setManaged(m);
      })
      .catch((e: unknown) =>
        setMessage({ ok: false, text: e instanceof Error ? e.message : 'Could not load.' }),
      );
    // Folders only suggest names for rules; without them a name is typed.
    mail
      .folders()
      .then(setFolders)
      .catch(() => setFolders([]));
    // Loaded once, when Settings opens.
  }, []);

  if (!settings) {
    return (
      <main className="main settings">
        {message ? <div className="alert">{message.text}</div> : <p>Loading…</p>}
      </main>
    );
  }
  const v = settings.vacation;
  // A cleared date removes the key rather than storing undefined.
  const setVacation = (change: { [K in keyof Vacation]?: Vacation[K] | undefined }) => {
    const next = { ...v, ...change } as Record<string, unknown>;
    for (const key of Object.keys(next)) {
      if (next[key] === undefined) {
        delete next[key];
      }
    }
    setSettings({ ...settings, vacation: next as unknown as Vacation });
  };
  return (
    <main className="main settings">
      <div className="main-head">
        <button type="button" className="btn ghost" style={{ width: 'auto' }} onClick={leave}>
          ← Mail
        </button>
        <h2>Settings</h2>
      </div>
      <SettingsIndex />
      {!managed && (
        <div className="alert">
          <strong>These rules were written by another program</strong>
          Saving here replaces them.
        </div>
      )}

      <section className="panel">
        <h3>Signature</h3>
        <p className="muted">
          Added to new messages, replies and forwards. Kept on the mail server.
        </p>
        <SignatureEditor
          value={settings.signature ?? ''}
          onChange={(html) => setSettings({ ...settings, signature: html })}
        />
      </section>

      <section className="panel">
        <h3>Writing format</h3>
        <p className="muted">
          How new messages, replies and forwards start. Either way, people whose mail app shows only
          plain text can read what you send.
        </p>
        <label className="check">
          <input
            type="radio"
            name="compose-format"
            checked={settings.composeFormat !== 'text'}
            onChange={() => {
              const { composeFormat: _format, ...rest } = settings;
              setSettings(rest);
            }}
          />
          Formatted (HTML): bold, lists, links and quotes
        </label>
        <label className="check">
          <input
            type="radio"
            name="compose-format"
            checked={settings.composeFormat === 'text'}
            onChange={() => setSettings({ ...settings, composeFormat: 'text' })}
          />
          Plain text only
        </label>
      </section>

      <ScheduledSending />

      <Notifications />

      <section className="panel">
        <h3>Out of office</h3>
        <label className="check">
          <input
            type="checkbox"
            checked={v.enabled}
            onChange={(e) => setVacation({ enabled: e.target.checked })}
          />
          Reply automatically
        </label>
        <div className="fields">
          <label>
            From
            <input
              type="date"
              value={v.start ?? ''}
              onChange={(e) => setVacation({ start: e.target.value || undefined })}
            />
          </label>
          <label>
            Until
            <input
              type="date"
              value={v.end ?? ''}
              onChange={(e) => setVacation({ end: e.target.value || undefined })}
            />
          </label>
          <label>
            Once per sender every
            <input
              type="number"
              min={1}
              max={30}
              value={v.days}
              onChange={(e) => setVacation({ days: Number(e.target.value) })}
            />
            days
          </label>
        </div>
        <input
          value={v.subject}
          onChange={(e) => setVacation({ subject: e.target.value })}
          placeholder="Subject (optional)"
          aria-label="Reply subject"
        />
        <textarea
          rows={5}
          value={v.body}
          onChange={(e) => setVacation({ body: e.target.value })}
          placeholder="I'm away until…"
          aria-label="Reply message"
        />
      </section>

      <ForwardingPanel
        tenant={tenant}
        value={settings.forwarding ?? NO_FORWARDING}
        onChange={(forwarding) => setSettings({ ...settings, forwarding })}
      />

      <RulesPanel rules={rules} folders={folders} onChange={setRules} />

      <div className="save-row">
        <button
          type="button"
          className="btn"
          style={{ width: 'auto' }}
          disabled={saving}
          onClick={() => {
            setSaving(true);
            setMessage(null);
            mail
              .saveSettings({ ...settings, rules: rules.map((r) => r.rule) })
              .then(() => {
                setManaged(true);
                setSaved(snapshot(settings, rules));
                setMessage({
                  ok: true,
                  text: 'Saved. They apply to the next message that arrives.',
                });
              })
              .catch((e: unknown) =>
                setMessage({ ok: false, text: e instanceof Error ? e.message : 'Not saved.' }),
              )
              .finally(() => setSaving(false));
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {message && !(message.ok && dirty) && (
          <span className={message.ok ? 'ok' : 'error'}>{message.text}</span>
        )}
        {dirty && !saving && <span className="muted">Unsaved changes</span>}
      </div>

      <SenderLists tenant={tenant} />

      <TwoStepSignIn tenant={tenant} />

      <PasswordRecovery tenant={tenant} />

      <MailApps tenant={tenant} />

      <ImportOldMail tenant={tenant} />
    </main>
  );
}

/**
 * Links to every section of the page, read from the sections' own headings
 * as they appear (several load on their own), so a long page is one click
 * from anything on it.
 */
function SettingsIndex(): JSX.Element | null {
  const ref = useRef<HTMLElement>(null);
  const [headings, setHeadings] = useState<HTMLElement[]>([]);
  useEffect(() => {
    const page = ref.current?.closest('main');
    if (!page) {
      return;
    }
    const scan = () => {
      const found = [...page.querySelectorAll<HTMLElement>('section.panel > h3')];
      setHeadings((old) =>
        old.length === found.length && old.every((h, i) => h === found[i]) ? old : found,
      );
    };
    scan();
    const watch = new MutationObserver(scan);
    watch.observe(page, { childList: true, subtree: true });
    return () => watch.disconnect();
  }, []);
  return (
    <nav ref={ref} className="settings-index" aria-label="Settings sections">
      {headings.map((h, i) => (
        <button
          type="button"
          key={`${i}-${h.textContent ?? ''}`}
          className="text-btn"
          onClick={() => h.scrollIntoView({ behavior: 'smooth', block: 'start' })}
        >
          {h.textContent}
        </button>
      ))}
    </nav>
  );
}

interface MySenders {
  readonly allowed: readonly string[];
  readonly blocked: readonly string[];
}

/**
 * The person's own blocked and allowed senders (migration 0039). Blocked:
 * filed in Junk. Allowed: never filed in Junk for a spam score. Both act on
 * new mail, for this mailbox only; viruses and the organisation's own rules
 * still apply.
 */
export function SenderLists({ tenant }: { tenant: string }): JSX.Element {
  const route = `/tenants/${encodeURIComponent(tenant)}/my-senders`;
  const [lists, setLists] = useState<MySenders | null>(null);
  const [entry, setEntry] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Whatever answered, two lists: an older server or an error page has none.
  const accept = (d: Partial<MySenders> | null) =>
    setLists({
      allowed: Array.isArray(d?.allowed) ? d.allowed : [],
      blocked: Array.isArray(d?.blocked) ? d.blocked : [],
    });

  useEffect(() => {
    const controller = new AbortController();
    api<Partial<MySenders>>(route, { signal: controller.signal })
      .then(accept)
      .catch((e: unknown) => {
        if (!controller.signal.aborted) {
          setProblem(describeError(e));
        }
      });
    return () => controller.abort();
  }, [route]);

  const save = (next: MySenders) => {
    setBusy(true);
    setProblem(null);
    api<Partial<MySenders>>(route, { method: 'PUT', body: next })
      .then((stored) => {
        accept(stored);
        setEntry('');
      })
      .catch((e: unknown) => setProblem(describeError(e)))
      .finally(() => setBusy(false));
  };
  const add = (list: keyof MySenders) => {
    if (!lists || !entry.trim()) {
      return;
    }
    const e = entry.trim().toLowerCase();
    const other = list === 'allowed' ? 'blocked' : 'allowed';
    save({
      ...lists,
      [list]: [...lists[list].filter((x) => x !== e), e],
      [other]: lists[other].filter((x) => x !== e),
    } as MySenders);
  };

  return (
    <section className="panel" aria-label="Blocked and allowed senders">
      <h3>Blocked and allowed senders</h3>
      <p className="muted">
        Mail from a blocked sender goes to Junk. Mail from an allowed sender is never put in Junk
        for looking like spam. Enter an address, or <code>@domain</code> for everyone there.
      </p>
      {problem && (
        <div className="alert" role="alert">
          {problem}
        </div>
      )}
      {lists && (
        <>
          <div className="save-row">
            <input
              value={entry}
              onChange={(e) => setEntry(e.target.value)}
              placeholder="name@example.com or @example.com"
              aria-label="Sender"
            />
            <button
              type="button"
              className="btn ghost"
              style={{ width: 'auto' }}
              disabled={busy || !entry.trim()}
              onClick={() => add('blocked')}
            >
              Block
            </button>
            <button
              type="button"
              className="btn ghost"
              style={{ width: 'auto' }}
              disabled={busy || !entry.trim()}
              onClick={() => add('allowed')}
            >
              Allow
            </button>
          </div>
          {(['blocked', 'allowed'] as const).map((list) => (
            <div key={list}>
              <h4>{list === 'blocked' ? 'Blocked' : 'Allowed'}</h4>
              {lists[list].length === 0 ? (
                <p className="muted">None.</p>
              ) : (
                <ul className="sender-list" aria-label={list === 'blocked' ? 'Blocked' : 'Allowed'}>
                  {lists[list].map((x) => (
                    <li key={x}>
                      <span className="addr">{x}</span>
                      <button
                        type="button"
                        className="text-btn"
                        disabled={busy}
                        aria-label={`Remove ${x}`}
                        onClick={() =>
                          save({
                            ...lists,
                            [list]: lists[list].filter((y) => y !== x),
                          } as MySenders)
                        }
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </>
      )}
    </section>
  );
}

/** New-mail notifications on this device (see notify.ts). */
function Notifications(): JSX.Element | null {
  const [on, setOn] = useState(notifyWanted);
  const [note, setNote] = useState<string | null>(null);
  if (!canNotify()) {
    return null;
  }
  return (
    <section className="panel">
      <h3>Notifications</h3>
      <label className="check">
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => {
            const want = e.target.checked;
            setNote(null);
            void setNotify(want).then((now) => {
              setOn(now);
              if (want && !now) {
                setNote(
                  'Notifications are blocked for webmail. Allow them in your browser or system settings, then try again.',
                );
              }
            });
          }}
        />
        Tell me about new mail on this device when webmail is not in front
      </label>
      {note && <p className="error">{note}</p>}
    </section>
  );
}

/** Whether scheduled sending is allowed, and a way to take it back. */
function ScheduledSending(): JSX.Element | null {
  const [state, setState] = useState<ScheduledList | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const load = () => {
    mail
      .scheduled()
      .then(setState)
      .catch(() => setState(null));
  };
  useEffect(load, []);
  if (!state?.available) {
    return null;
  }
  const waiting = state.messages.filter((m) => m.state !== 'failed').length;
  return (
    <section className="panel">
      <h3>Scheduled sending</h3>
      {state.allowed ? (
        <>
          <p className="muted">
            Allowed. ZitiMail holds a delegation to send your scheduled messages while you are away
            {waiting > 0 ? ` — ${waiting} waiting to go` : ''}. It ends by itself when nothing is
            left to send.
          </p>
          <button
            type="button"
            className="btn ghost"
            style={{ width: 'auto' }}
            onClick={() =>
              void mail
                .revokeScheduling()
                .then(() => {
                  setMessage('Revoked. Anything that was scheduled is back in Drafts.');
                  load();
                })
                .catch((e: unknown) => setMessage(e instanceof Error ? e.message : 'Not revoked.'))
            }
          >
            Revoke
          </button>
        </>
      ) : (
        <p className="muted">
          Not allowed yet. You are asked the first time you schedule a message.
        </p>
      )}
      {message && <p className="notice">{message}</p>}
    </section>
  );
}

/**
 * Forward every incoming message to another address, on the server. Whether
 * it may leave the organisation is the tenant admin's call; the mail plane
 * enforces it, and this says so before the user finds out by missing mail.
 */
function ForwardingPanel({
  tenant,
  value,
  onChange,
}: {
  tenant: string;
  value: Forwarding;
  onChange: (next: Forwarding) => void;
}): JSX.Element {
  const [policy, setPolicy] = useState<ForwardingPolicy | null>(null);
  useEffect(() => {
    api<ForwardingPolicy>(`/tenants/${encodeURIComponent(tenant)}/forwarding-policy`)
      .then(setPolicy)
      .catch(() => setPolicy(null));
  }, [tenant]);

  const domain = value.address.split('@')[1]?.toLowerCase() ?? '';
  const outside =
    policy !== null && domain !== '' && !policy.domains.some((d) => d.toLowerCase() === domain);
  const refused = value.enabled && outside && policy?.external_forwarding_allowed === false;

  return (
    <section className="panel">
      <h3>Forwarding</h3>
      <p className="muted">
        Sends every message you receive on to another address, even when webmail is closed.
        {policy?.external_forwarding_allowed === false &&
          ' Your organisation only allows forwarding to its own addresses.'}
      </p>
      <label className="check">
        <input
          type="checkbox"
          checked={value.enabled}
          onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
        />
        Forward my mail
      </label>
      <input
        type="email"
        value={value.address}
        onChange={(e) => onChange({ ...value, address: e.target.value.trim() })}
        placeholder="name@example.com"
        aria-label="Forward to"
      />
      <label className="check">
        <input
          type="checkbox"
          checked={value.keepCopy}
          onChange={(e) => onChange({ ...value, keepCopy: e.target.checked })}
        />
        Keep a copy here
      </label>
      {refused && (
        <div className="alert" role="alert">
          <strong>Your organisation does not allow forwarding to {domain}</strong>
          Messages stay in this mailbox instead. Ask your administrator if you need this.
        </div>
      )}
      {!refused && value.enabled && !value.keepCopy && (
        <p className="muted">Messages will not be kept here, and your rules will not run.</p>
      )}
    </section>
  );
}

type Provider = 'imap' | 'gmail' | 'office365';

const PROVIDER_HOST: Record<Provider, string> = {
  imap: '',
  gmail: 'imap.gmail.com',
  office365: 'outlook.office365.com',
};

const DEFAULT_PORT = { ssl: 993, starttls: 143 } as const;

/**
 * Copy the mail from an old mailbox into this one. The server does the
 * copying, so it carries on with webmail closed; the old mailbox is only read.
 */
/**
 * Mail apps that cannot sign in through a browser — Apple Mail, the phone
 * mail apps, scripts — use an app password instead (ADR-0038). One per app,
 * shown once; removing it signs that app out and nothing else.
 */
/**
 * The person's recovery email (ADR-0053): proven with a code, it is where a
 * forgotten password sends its link. Offered only when the password is ours;
 * a directory or identity-provider sign-in shows the organisation's help.
 */
export function PasswordRecovery({ tenant }: { tenant: string }): JSX.Element | null {
  const route = `/tenants/${encodeURIComponent(tenant)}/my-recovery`;
  const [status, setStatus] = useState<RecoveryStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [address, setAddress] = useState('');
  const [code, setCode] = useState('');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(() => {
    api<RecoveryStatus>(route)
      .then((found) => {
        setStatus(found);
        setError(null);
      })
      .catch((e: unknown) => setError(describeError(e)));
  }, [route]);
  useEffect(reload, [reload]);

  const run = (work: Promise<unknown>, done: string) => {
    setBusy(true);
    setError(null);
    setNote(null);
    work
      .then(() => {
        setNote(done);
        reload();
      })
      .catch((e: unknown) => setError(describeError(e)))
      .finally(() => setBusy(false));
  };
  const sendCode = (to: string) =>
    run(
      api(route, { method: 'POST', body: { address: to.trim() } }).then(() => {
        setCode('');
        setEditing(false);
      }),
      `A code is on its way to ${to.trim()}.`,
    );

  if (!status) {
    return error ? (
      <section className="panel" aria-label="Password recovery">
        <h3>Password recovery</h3>
        <p className="error">{error}</p>
      </section>
    ) : null;
  }
  if (status.password_owner === 'no_sign_in') {
    return null;
  }
  if (!status.offered) {
    return (
      <section className="panel" aria-label="Password recovery">
        <h3>Password recovery</h3>
        <p className="muted">
          Your organisation manages your password.{' '}
          {status.help_url ? (
            <>
              To reset it, use{' '}
              <a href={status.help_url} target="_blank" rel="noreferrer">
                your organisation&apos;s password help
              </a>
              .
            </>
          ) : (
            'To reset it, ask your IT team.'
          )}
        </p>
      </section>
    );
  }
  return (
    <section className="panel" aria-label="Password recovery">
      <h3>Password recovery</h3>
      <p className="muted">
        A personal email address, not your work one. If you forget your password, a link to choose a
        new one goes there. Your authenticator still protects your account.
      </p>
      {!status.mail_ready && (
        <p className="muted">Recovery email is not available yet. Ask your administrator.</p>
      )}

      {status.address && !editing && (
        <div className="save-row">
          <span>
            Recovery email: <strong>{status.address}</strong>
            {status.verified_at && (
              <span className="muted"> · confirmed {when(status.verified_at)}</span>
            )}
          </span>
          <button
            type="button"
            className="btn ghost"
            style={{ width: 'auto', marginLeft: 'auto' }}
            onClick={() => {
              setAddress('');
              setEditing(true);
            }}
            disabled={!status.mail_ready}
          >
            Change
          </button>
          <button
            type="button"
            className="btn ghost"
            style={{ width: 'auto' }}
            onClick={() => {
              if (
                window.confirm(
                  'Remove your recovery email? A forgotten password then needs your administrator.',
                )
              ) {
                run(api(route, { method: 'DELETE' }), 'Recovery email removed.');
              }
            }}
          >
            Remove
          </button>
        </div>
      )}

      {status.pending_address && !editing && (
        <form
          className="import-form"
          aria-label="Confirm recovery email"
          onSubmit={(event) => {
            event.preventDefault();
            run(
              api(`${route}/verify`, { method: 'POST', body: { code: code.trim() } }).then(() =>
                setCode(''),
              ),
              'Recovery email confirmed.',
            );
          }}
        >
          <p>
            Enter the code sent to <strong>{status.pending_address}</strong>.
          </p>
          <div className="save-row">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123456"
              aria-label="Code"
            />
            <button
              type="submit"
              className="btn"
              style={{ width: 'auto' }}
              disabled={busy || code.length !== 6}
            >
              Confirm
            </button>
            <button
              type="button"
              className="btn ghost"
              style={{ width: 'auto' }}
              disabled={busy}
              onClick={() => sendCode(status.pending_address ?? '')}
            >
              Send a new code
            </button>
          </div>
        </form>
      )}

      {status.mail_ready && (editing || (!status.address && !status.pending_address)) && (
        <form
          className="import-form"
          aria-label="Set recovery email"
          onSubmit={(event) => {
            event.preventDefault();
            sendCode(address);
          }}
        >
          <div className="save-row">
            <input
              type="email"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="you@personal-mail.com"
              aria-label="Recovery email"
              maxLength={254}
            />
            <button
              type="submit"
              className="btn"
              style={{ width: 'auto' }}
              disabled={busy || address.trim() === ''}
            >
              {busy ? 'Sending…' : 'Send code'}
            </button>
            {editing && (
              <button
                type="button"
                className="btn ghost"
                style={{ width: 'auto' }}
                onClick={() => setEditing(false)}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      )}

      {note && <p className="ok">{note}</p>}
      {error && <p className="error">{error}</p>}
    </section>
  );
}

function MailApps({ tenant }: { tenant: string }): JSX.Element {
  const route = `/tenants/${encodeURIComponent(tenant)}/my-app-passwords`;
  const [list, setList] = useState<AppPasswordList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState<CreatedAppPassword | null>(null);
  const [servers, setServers] = useState<MailServers | null>(null);
  const [copied, setCopied] = useState(false);

  const reload = useCallback(() => {
    api<AppPasswordList>(route)
      .then((found) => {
        setList(found);
        setError(null);
      })
      .catch((e: unknown) => setError(describeError(e)));
  }, [route]);
  useEffect(reload, [reload]);
  useEffect(() => {
    // Only for showing beside a new password; without it the password still works.
    fetch('/api/mail-servers', { credentials: 'include' })
      .then((r) => (r.ok ? (r.json() as Promise<MailServers>) : null))
      .then(setServers)
      .catch(() => setServers(null));
  }, []);

  const create = () => {
    setBusy(true);
    setError(null);
    setCopied(false);
    api<CreatedAppPassword>(route, { method: 'POST', body: { label: label.trim() } })
      .then((made) => {
        setCreated(made);
        setLabel('');
        reload();
      })
      .catch((e: unknown) => setError(describeError(e)))
      .finally(() => setBusy(false));
  };

  const remove = (item: AppPassword) => {
    if (!window.confirm(`Remove the app password for ${item.label}? That app is signed out.`)) {
      return;
    }
    setError(null);
    api(`${route}/${encodeURIComponent(item.id)}`, { method: 'DELETE' })
      .then(() => {
        if (created?.id === item.id) {
          setCreated(null);
        }
        reload();
      })
      .catch((e: unknown) => setError(describeError(e)));
  };

  const full = list !== null && list.app_passwords.length >= list.max;
  return (
    <section className="panel" aria-label="Mail apps">
      <h3>Mail apps</h3>
      <p className="muted">
        Thunderbird signs in with your organisation&apos;s sign-in page: add the account and it
        finds everything itself. Apple Mail, the iPhone and Android mail apps and other programs
        need an app password instead — make one for each, so a lost phone can be removed on its own.
      </p>

      {list && !list.allowed && (
        <p className="muted">
          Your organisation does not allow app passwords. Use webmail or Thunderbird, or ask your IT
          team.
        </p>
      )}

      {created && (
        <div className="import-job app-password-new" role="status">
          <strong>App password for {created.label}</strong>
          <p>Use it in place of your password. It is shown only now.</p>
          <div className="save-row">
            <code className="app-password">{created.password}</code>
            <button
              type="button"
              className="btn ghost"
              style={{ width: 'auto' }}
              onClick={() => {
                void navigator.clipboard
                  ?.writeText(created.password)
                  .then(() => setCopied(true))
                  .catch(() => setCopied(false));
              }}
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <dl className="mail-servers">
            <dt>User name</dt>
            <dd>{created.username}</dd>
            {servers && (
              <>
                <dt>Incoming (IMAP)</dt>
                <dd>
                  {servers.imap.host}, port {servers.imap.port}, {servers.imap.security}
                </dd>
                <dt>Outgoing (SMTP)</dt>
                <dd>
                  {servers.smtp.host}, port {servers.smtp.port}, {servers.smtp.security}
                </dd>
              </>
            )}
          </dl>
          <div className="save-row">
            <button
              type="button"
              className="btn ghost"
              style={{ width: 'auto' }}
              onClick={() => setCreated(null)}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {list?.app_passwords.map((item) => (
        <div key={item.id} className="import-job" aria-label={`App password for ${item.label}`}>
          <div className="save-row">
            <strong>{item.label}</strong>
            <span className="muted">made {when(item.created_at)}</span>
            <button
              type="button"
              className="btn ghost"
              style={{ width: 'auto', marginLeft: 'auto' }}
              onClick={() => remove(item)}
            >
              Remove
            </button>
          </div>
        </div>
      ))}

      {error && <p className="error">{error}</p>}

      {list?.allowed && (
        <form
          className="import-form"
          onSubmit={(event) => {
            event.preventDefault();
            create();
          }}
        >
          <div className="save-row">
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="iPhone Mail"
              aria-label="App or device"
              maxLength={64}
              disabled={full}
            />
            <button
              type="submit"
              className="btn"
              style={{ width: 'auto' }}
              disabled={busy || full || label.trim() === ''}
            >
              {busy ? 'Making…' : 'Make an app password'}
            </button>
          </div>
          {full && (
            <p className="muted">
              You have {list.max}, the most allowed. Remove one you no longer use first.
            </p>
          )}
        </form>
      )}
    </section>
  );
}

function ImportOldMail({ tenant }: { tenant: string }): JSX.Element {
  const route = `/tenants/${encodeURIComponent(tenant)}/my-import`;
  const [jobs, setJobs] = useState<readonly MigrationJob[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const reload = useCallback(() => {
    api<{ jobs: MigrationJob[] }>(route)
      .then(({ jobs: found }) => {
        setJobs(found);
        setLoadError(null);
      })
      .catch((e: unknown) => setLoadError(describeError(e)));
  }, [route]);
  useEffect(reload, [reload]);
  // Keeps trying after a failed refresh, so a passing outage clears itself.
  const moving = loadError !== null || (jobs?.some(inMotion) ?? false);
  usePoll(moving, reload);

  const [provider, setProvider] = useState<Provider>('imap');
  const [host, setHost] = useState('');
  const [security, setSecurity] = useState<'ssl' | 'starttls'>('ssl');
  const [port, setPort] = useState<number>(DEFAULT_PORT.ssl);
  const [user, setUser] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = () => {
    const row: MigrationSourceRow = {
      source_host: host.trim(),
      source_port: port,
      source_security: security,
      source_user: user.trim(),
      password,
      source_kind: provider,
    };
    setBusy(true);
    setError(null);
    api(route, { method: 'POST', body: row })
      .then(() => {
        setPassword('');
        reload();
      })
      .catch((e: unknown) => setError(describeError(e)))
      .finally(() => setBusy(false));
  };

  const act = (job: MigrationJob, action: 'sync' | 'cancel') => {
    setError(null);
    api(`${route}/${encodeURIComponent(job.id)}/${action}`, { method: 'POST' })
      .then(reload)
      .catch((e: unknown) => setError(describeError(e)));
  };

  return (
    <section className="panel">
      <h3>Import my old email</h3>
      <p className="muted">
        Copies the mail from another mailbox into this one, folders included. Your old mailbox is
        not changed. Your password for it is kept encrypted while the import can still run, and
        deleted when you stop the import or it is finished.
      </p>

      {jobs?.map((job) => {
        const note = statusNote(job);
        const closed = job.status === 'cancelled' || job.status === 'done';
        const again = job.has_credential && (job.status === 'synced' || job.status === 'failed');
        return (
          <div key={job.id} className="import-job" aria-label={`Import from ${job.source_host}`}>
            <div>
              <strong>
                {job.source_user} on {job.source_host}
              </strong>{' '}
              <StatusBadge job={job} />
            </div>
            <Progress job={job} />
            {note && <p className="muted">{note}</p>}
            {job.last_synced_at && <p className="muted">Last copied {when(job.last_synced_at)}.</p>}
            {job.last_error && job.status !== 'running' && <ErrorText text={job.last_error} />}
            {!closed && (
              <div className="save-row">
                {again && (
                  <button
                    type="button"
                    className="btn ghost"
                    style={{ width: 'auto' }}
                    onClick={() => act(job, 'sync')}
                  >
                    {job.status === 'failed' ? 'Try again' : 'Copy new mail now'}
                  </button>
                )}
                <button
                  type="button"
                  className="btn ghost"
                  style={{ width: 'auto' }}
                  onClick={() => {
                    if (
                      window.confirm(
                        'Stop this import? Mail already copied stays here; your password for the old mailbox is deleted.',
                      )
                    ) {
                      act(job, 'cancel');
                    }
                  }}
                >
                  Stop
                </button>
              </div>
            )}
          </div>
        );
      })}

      {loadError && <p className="error">{loadError}</p>}

      {jobs !== null && !moving && (
        <form
          className="import-form"
          onSubmit={(event) => {
            event.preventDefault();
            start();
          }}
        >
          <div className="fields">
            <label>
              Provider
              <select
                value={provider}
                onChange={(e) => {
                  const next = e.target.value as Provider;
                  setProvider(next);
                  setHost(PROVIDER_HOST[next]);
                  if (next !== 'imap') {
                    setSecurity('ssl');
                    setPort(DEFAULT_PORT.ssl);
                  }
                }}
              >
                <option value="imap">Other IMAP server</option>
                <option value="gmail">Gmail</option>
                <option value="office365">Microsoft 365</option>
              </select>
            </label>
          </div>
          {provider === 'gmail' && (
            <p className="muted">
              Use an app password, not your Google password: Google Account → Security → App
              passwords. IMAP must be on in Gmail&apos;s settings.
            </p>
          )}
          {provider === 'office365' && (
            <p className="muted">
              Microsoft 365 normally refuses passwords over IMAP and needs an app registration set
              up by your administrator, so this may be refused.
            </p>
          )}
          <input
            value={host}
            onChange={(e) => setHost(e.target.value.trim())}
            placeholder="imap.example.com"
            aria-label="Server"
            required
          />
          <div className="fields">
            <label>
              Security
              <select
                value={security}
                onChange={(e) => {
                  const next = e.target.value as 'ssl' | 'starttls';
                  setSecurity(next);
                  setPort(DEFAULT_PORT[next]);
                }}
              >
                <option value="ssl">SSL/TLS (port 993)</option>
                <option value="starttls">STARTTLS (port 143)</option>
              </select>
            </label>
            <label>
              Port
              <input
                type="number"
                min={1}
                max={65535}
                value={port}
                onChange={(e) => setPort(Number(e.target.value))}
              />
            </label>
          </div>
          <input
            value={user}
            onChange={(e) => setUser(e.target.value)}
            placeholder="Sign-in name, often your old email address"
            aria-label="Sign-in name"
            autoComplete="off"
            required
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            aria-label="Password for the old mailbox"
            autoComplete="new-password"
            required
          />
          <div className="save-row">
            <button type="submit" className="btn" style={{ width: 'auto' }} disabled={busy}>
              {busy ? 'Starting…' : 'Start import'}
            </button>
            {error && <span className="error">{error}</span>}
          </div>
        </form>
      )}
      {moving && error && <p className="error">{error}</p>}
    </section>
  );
}
